/**
 * Bridge for Stripe Embedded Checkout data between session creation and the
 * embedded pay route.
 *
 * Both providers navigate to the same pay route keyed by `sessionId` in the URL.
 * Dintero re-embeds from that id alone, but Stripe Embedded Checkout also needs
 * the `clientSecret` + publishable key + connected account, which are not derivable
 * from the id on the client. We stash them in `sessionStorage` keyed by session id
 * so the pay route can pick the right widget and survive a page refresh within the
 * tab. The client secret is meant for the browser (Stripe.js consumes it), so this
 * is no more sensitive than passing it through router state.
 */

const STORAGE_PREFIX = 'stripe_embedded_session:';

export interface StripeEmbeddedSession {
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string | null;
  countryCode: string;
}

const storageKey = (sessionId: string): string => `${STORAGE_PREFIX}${sessionId}`;

export const saveStripeEmbeddedSession = (sessionId: string, session: StripeEmbeddedSession): void => {
  try {
    sessionStorage.setItem(storageKey(sessionId), JSON.stringify(session));
  } catch {
    // sessionStorage may be unavailable (private mode / quota); the pay route
    // falls back to a "session expired" state when nothing is stored.
  }
};

export const getStripeEmbeddedSession = (sessionId: string): StripeEmbeddedSession | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(sessionId));
    return raw ? (JSON.parse(raw) as StripeEmbeddedSession) : null;
  } catch {
    return null;
  }
};
